/**
 * api/ip.ts — `/api/ip*` endpoints.
 *
 * Carved from `src/composables/useIPInfo.ts` per A6 §2. The composable
 * keeps its reactive-state surface (`clientIp`, `ipInfo`, `lookedUpIp`,
 * `isLoading`); the HTTP calls move here.
 *
 * `getClientIp()` returns the IP as a trimmed plain-text string —
 * the endpoint emits `text/plain`, not JSON, which is why the
 * implementation uses `apiFetchRaw` rather than `apiFetch<T>`.
 * The `ipify` fallback also lives here; the composable now reads the
 * resilient `getClientIp()` shape rather than juggling two fetch
 * sites.
 */

import { apiFetchRaw } from "./client.ts";
import type { IPInfo, LookedUpIP } from "@src/types/dns.ts";

const IPIFY_TIMEOUT_MS = 3000;

/**
 * IPv4 + IPv6 shape regex. Tight enough to reject JSON error bodies
 * (`{"error":"proxy_unreachable",...}`) and stray whitespace; loose
 * enough that the proxy can return either family. The exact byte-range
 * check (octets 0-255, IPv6 segment shape) is intentionally not the
 * point — the *category* check (is this even IP-shaped vs. a JSON
 * payload or HTML fragment?) is what closes the §5.D-12 raw-JSON leak.
 *
 *   IPv4: 1-3 digits, four times, dot-separated.
 *   IPv6: any non-empty run of hex digits + colons + optional `::`
 *         compression; allow trailing dotted-IPv4 suffix for mapped
 *         addresses.
 *
 * Anything else (curly braces, angle brackets, quotes, spaces inside
 * the body) falls through to the ipify fallback path.
 */
const IP_SHAPE_RE = /^(?:(?:\d{1,3}\.){3}\d{1,3}|[0-9a-fA-F:]+(?:\.\d{1,3}){0,3})$/;

/**
 * Fetch the client's public IP via the Friday Institute endpoint, with
 * an ipify.org fallback under a 3s timeout. Returns the plain-text IP.
 *
 * The ipify fallback carries an `AbortController` timeout so hostile
 * networks (corporate firewalls, captive portals) can't let the fetch
 * hang for the full Linux TCP timeout (~75s).
 *
 * AJ.W3-θ (A3 §5.D-12) — defensive validation: `resp.ok` MUST be true
 * before we read the body, and the body MUST match an IP-shape regex.
 * The previous contract only guarded against an HTML response (`<!` /
 * `<html`) which let JSON error bodies (`{"error":"proxy_unreachable",
 * "code":"ENOTFOUND",...}`) fall through as "valid IP" strings — those
 * then surfaced as raw JSON in the App Settings Connection display.
 * Both new guards throw → the catch-block routes to the ipify fallback,
 * which is the natural recovery path for any backend-unreachable shape.
 */
export async function getClientIp(): Promise<string> {
    try {
        const resp = await apiFetchRaw("/api/ip");
        if (!resp.ok) {
            throw new Error(`/api/ip returned ${resp.status}`);
        }
        const text = (await resp.text()).trim();
        // Guard against Vite SPA fallback returning HTML.
        if (text.startsWith("<!") || text.startsWith("<html")) {
            throw new Error("Got HTML instead of IP");
        }
        // Guard against JSON error bodies (or any non-IP-shaped payload)
        // surfacing as the client's IP. See §5.D-12.
        if (!IP_SHAPE_RE.test(text)) {
            throw new Error("Response body is not IP-shaped");
        }
        return text;
    } catch {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), IPIFY_TIMEOUT_MS);
        try {
            const resp = await fetch("https://api.ipify.org?format=json", {
                signal: controller.signal,
            });
            const data = (await resp.json()) as { ip: string };
            return data.ip;
        } finally {
            clearTimeout(timer);
        }
    }
}

/**
 * Fetch ISP/geographic metadata for an IP. The endpoint emits JSON
 * with a content-type guard — the composable's prior `if
 * (!contentType.includes("json")) throw` shape lives here too because
 * the SPA fallback can serve HTML on this path.
 */
export async function getIpInfo(ip: string): Promise<IPInfo> {
    const resp = await apiFetchRaw(`/api/ip/info/${encodeURIComponent(ip)}`);
    const contentType = resp.headers.get("content-type") ?? "";
    if (!contentType.includes("json")) throw new Error("Not JSON");
    return (await resp.json()) as IPInfo;
}

/** Look up the entity (org/ASN/region) associated with an IP. */
export async function lookupIp(ip: string): Promise<LookedUpIP> {
    const resp = await apiFetchRaw(`/api/ip/lookup/${encodeURIComponent(ip)}`);
    const contentType = resp.headers.get("content-type") ?? "";
    if (!contentType.includes("json")) throw new Error("Not JSON");
    return (await resp.json()) as LookedUpIP;
}
